import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  Box, 
  Typography, 
  MenuItem, 
  Select, 
  CircularProgress,  
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import api from 'state/axios';
import { boxStyles as styles } from 'components/CustomStyles';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import RequestCard from './RequestCard';


const CrewRequests = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  
  const { startDate, endDate } = useDateRangeContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);  

  const [requests, setRequests] = useState([]);
  const [statusState, setStatusState] = useState("All");
  const crewCode = user.crewCode;

  const fetchRequests = useCallback(async () => {
    if (!startDate || !endDate) return;

    try {
      setError(null);
      setLoading(true);
      const response = await api.post(
        '/crewrequests/requestsbycrewcode', 
        { crewCode, startDate, endDate }
      );
      setRequests(response.data?.data || []);
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch requests!");
    } finally {
      setLoading(false);
    }
  }, [crewCode, startDate, endDate]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

        
  const filteredRequests = useMemo(() => {
    if (!Array.isArray(requests)) return [];
    if (statusState === "All") return requests;

    return requests.length > 0 && requests?.filter((request) => request.status?.status === statusState)
  }, [requests, statusState]);
  
  
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Requests" 
          subtitle={`${loading ? 'Loading...' : 'List of Requests'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>     
            <Select
              variant="outlined"
              size="small"
              value={statusState}
              onChange={(e) => setStatusState(e.target.value)}
              sx={{ minWidth: 100 }}
            >
              {["All", "Pending", "Approved", "Rejected"]?.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>    
            <StyledButton 
              onClick={() => navigate("/crewrequests/new")}
            >
              <AddCircleOutlineIcon />
            </StyledButton>
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter sx={{ ...styles }}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter sx={{ ...styles }}>
          <Typography color="error">{error?.message}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >            
          {filteredRequests.length > 0 ? (
            filteredRequests.map((request, idx) => (
              <Box key={idx} sx={{ gridColumn: "span 6" }}>
                <RequestCard
                  key={request._id}
                  request={request}
                  onDeleteSuccess={fetchRequests}
                />
              </Box>
            ))
          ) : (
            <FlexCenter sx={{ ...styles, gridColumn: 'span 12' }}>
              <Typography color="error">No requests found</Typography>
            </FlexCenter>
          )}                    
        </Box>
      )}
    </Box>
  );
};

export default CrewRequests;
